import { useLogger } from './useLogger'
import { useNuxtApp } from '#imports'

// Analytics Event Names
export const AUTH_EVENTS = {
  LOGIN_STEP_1: 'login_step_1',
  LOGIN_STEP_2: 'login_step_2',
  SIGNUP_VIEW: 'signup_view',
  SIGNUP_STEP_1: 'signup_step_1',
  SIGNUP_STEP_2: 'signup_step_2',
  SIGNUP_STEP_3: 'signup_step_3',
  SIGNUP_COMPLETE: 'signup_complete',
  GOOGLE_AUTH_SUCCESS: 'google_auth_success',
  SIGNUP_METHOD_SELECTED_GOOGLE: 'signup_method_selected_google',
  SIGNUP_METHOD_SELECTED_EMAIL: 'signup_method_selected_email',
  ONBOARDING_WIZ_STEP_BASE: 'onb_wiz_',
} as const

export const ONBOARDING_EVENTS = {
  FIRST_TIME_ON_SIGNIN_SIGNON: 'first_time_on_signin_signon',
  FIRST_TIME_DEVICE_LAUNCH: 'first_time_device_launch',
  START_WIZARD_PAGE_VIEWED: 'start_wizard_page_viewed',
  START_WIZARD_TILE_COMPLETED: 'start_wizard_tile_completed',
  BUBBLE_SHOWN_BASE: 'bubble_shown',
  BUBBLE_DISMISSED_BASE: 'bubble_dismissed',
} as const

// Usage Events
export const USAGE_EVENTS = {
  CALENDAR_ITEM_ADDED: 'calendar_item_added',
  // Voice Chat Events
  VOICE_CHAT_SESSION_START: 'voice_chat_session_start',
  VOICE_CHAT_SESSION_END: 'voice_chat_session_end',
  VOICE_CHAT_USER_SPEECH: 'voice_chat_user_speech',
  VOICE_CHAT_TRANSCRIPTION_SUCCESS: 'voice_chat_transcription_success',
  VOICE_CHAT_TRANSCRIPTION_FAIL: 'voice_chat_transcription_fail',
  VOICE_CHAT_FUNCTION_CALL: 'voice_chat_function_call',
  VOICE_CHAT_ERROR: 'voice_chat_error'
} as const

export const NAVIGATION_EVENTS = {
  NAV_TILE_CLICK: 'nav_tile_click',
  SCREEN_VIEW: '$screen'
} as const

export const usePostHog = () => {
  const logger = useLogger()
  const nuxtApp = useNuxtApp()

  // Ensure posthog is available
  if (!nuxtApp.$posthog) {
    logger.debug('PostHog: Not initialized')
    return {
      capture: async () => {},
      identify: async () => {},
      reset: async () => {},
      register: async () => {}
    }
  }

  const posthog = nuxtApp.$posthog()

  return {
    capture: async (eventName: string, properties?: Record<string, any>) => {
      logger.debug('PostHog: Capturing event', { eventName, properties })
      try {
        if (posthog) {
          posthog.capture(eventName, {
            timestamp: new Date().toISOString(),
            ...properties
          })
          logger.debug('PostHog: Event captured', { eventName, properties })
        } else {
          logger.debug('PostHog: Not initialized')
        }
      } catch (error) {
        logger.logErrorCaught('PostHog: Failed to capture event', 'usePostHog.ts', error, { eventName, properties })
      }
    },

    identify: async (distinctId: string) => {
      try {
        if (posthog) {
          posthog.identify(distinctId)
          logger.debug('PostHog: User identified', { distinctId })
        } else {
          logger.debug('PostHog: Not initialized')
        }
      } catch (error) {
        logger.logErrorCaught('PostHog: Failed to identify user', 'usePostHog.ts', error, { distinctId })
        throw error
      }
    },

    reset: async () => {
      try {
        if (posthog) {
          posthog.reset()
          logger.debug('PostHog: Identity reset')
        } else {
          logger.debug('PostHog: Not initialized')
        }
      } catch (error) {
        logger.logErrorCaught('PostHog: Failed to reset identity', 'usePostHog.ts', error)
        throw error
      }
    },

    register: async (properties: Record<string, any>) => {
      try {
        if (posthog) {
          posthog.register(properties)
          logger.debug('PostHog: Properties registered', { properties })
        } else {
          logger.debug('PostHog: Not initialized')
        }
      } catch (error) {
        logger.logErrorCaught('PostHog: Failed to register properties', 'usePostHog.ts', error, { properties })
        throw error
      }
    }
  }
} 